// Translations for creator subscription module (fan + studio + admin)

import type { TranslationDict } from '../types';

export const creatorSubscription: TranslationDict = {
  // Studio shell
  studioShell: {
    title: { en: 'Creator Studio', 'zh-CN': '创作者中心', ja: 'クリエイタースタジオ' },
    subtitle: {
      en: 'Manage your monetization, subscribers, and content tools.',
      'zh-CN': '管理变现、订阅者和创作工具。',
      ja: '収益化・サブスクライバー・コンテンツツールを管理。',
    },
    backToStudio: { en: '← Studio', 'zh-CN': '← 创作者中心', ja: '← スタジオへ戻る' },
    help: { en: 'Help', 'zh-CN': '帮助', ja: 'ヘルプ' },
    cards: {
      monetization: {
        title: { en: 'Make money on Kommend', 'zh-CN': '在 Kommend 上变现', ja: 'Kommend で収益化' },
        desc: {
          en: 'Subscriptions and post earnings overview.',
          'zh-CN': '订阅与帖子分成总览。',
          ja: 'サブスクと投稿収益の概要。',
        },
      },
      subscriptions: {
        title: { en: 'Subscriptions', 'zh-CN': '开通会员订阅', ja: 'サブスク' },
        desc: {
          en: 'Manage tiers, see members, view payouts.',
          'zh-CN': '管理订阅等级、查看会员和结算。',
          ja: '階層管理、メンバー確認、ペイアウト閲覧。',
        },
      },
      analytics: {
        title: { en: 'Analytics', 'zh-CN': '数据分析', ja: 'アナリティクス' },
        desc: { en: 'Coming soon.', 'zh-CN': '即将上线', ja: '近日公開' },
      },
      help: {
        title: { en: 'Help & docs', 'zh-CN': '帮助与文档', ja: 'ヘルプとドキュメント' },
        desc: {
          en: 'Read program rules, eligibility, and FAQs.',
          'zh-CN': '阅读项目规则、资格和常见问题。',
          ja: 'ルール・資格・FAQを読む。',
        },
      },
    },
  },

  // Subscriptions sub-tabs
  subTabs: {
    tiers: { en: 'Tiers', 'zh-CN': '等级', ja: '階層' },
    members: { en: 'Members', 'zh-CN': '会员', ja: 'メンバー' },
    stats: { en: 'Stats', 'zh-CN': '数据', ja: '統計' },
    payouts: { en: 'Payouts', 'zh-CN': '结算', ja: 'ペイアウト' },
  },

  // Tier card / editor (translated)
  tierCard: {
    perksHeading: { en: 'Perks', 'zh-CN': '权益', ja: '特典' },
  },
  tierEditorFull: {
    namePlaceholder: { en: 'e.g. Basic / Premium / VIP', 'zh-CN': '例：基础 / 高级 / VIP', ja: '例：ベーシック / プレミアム / VIP' },
    priceHelper: {
      en: 'Internal: SQR (1:1 USDC).',
      'zh-CN': '内部以 SQR 计价（1:1 USDC）。',
      ja: '内部はSQR建（1:1 USDC）。',
    },
    nameLabel: { en: 'Tier name', 'zh-CN': '等级名称', ja: '階層名' },
    descLabel: { en: 'Description (optional)', 'zh-CN': '描述（选填）', ja: '説明（任意）' },
    priceLabel: { en: 'Price (USD/month)', 'zh-CN': '价格（美元/月）', ja: '価格（USD/月）' },
    orderLabel: { en: 'Display order', 'zh-CN': '显示顺序', ja: '表示順' },
    perksLabel: { en: 'Perks', 'zh-CN': '权益', ja: '特典' },
    priceLockedHint: {
      en: 'Price cannot be changed in V1.',
      'zh-CN': 'V1 不支持改价。',
      ja: 'V1では価格変更不可。',
    },
    saveBtn: { en: 'Save changes', 'zh-CN': '保存修改', ja: '変更を保存' },
    createBtn: { en: 'Create tier', 'zh-CN': '创建等级', ja: '階層を作成' },
  },

  // Required-eligibility gate
  gate: {
    title: { en: 'Almost there', 'zh-CN': '还差一点', ja: 'もう少し' },
    body: {
      en: 'You need to meet the following requirements before you can offer subscriptions:',
      'zh-CN': '在开始接受订阅之前，你还需要达到以下条件：',
      ja: 'サブスクを始める前に以下の条件を満たす必要があります：',
    },
    backBtn: { en: 'Back to Monetization', 'zh-CN': '返回变现首页', ja: '収益化トップへ' },
  },

  // Eligibility labels
  eligibility: {
    isPaidMember: {
      en: 'Be a paying Kommend member (subscription or staking)',
      'zh-CN': '成为 Kommend 付费会员（订阅或质押）',
      ja: 'Kommend 有料メンバー（サブスクまたはステーキング）になる',
    },
    memberFollowers: {
      en: '{{required}} Kommend member followers — you have {{value}}',
      'zh-CN': '{{required}} 个 Kommend 会员粉丝 — 当前 {{value}}',
      ja: '{{required}} 人の Kommend メンバーフォロワー — 現在 {{value}}',
    },
    impressions3m: {
      en: '{{required}} post impressions in last 3 months — you have {{value}}',
      'zh-CN': '过去 3 个月内 {{required}} 次帖子曝光 — 当前 {{value}}',
      ja: '過去 3 ヶ月で {{required}} 回の投稿表示 — 現在 {{value}}',
    },
    posted30d: {
      en: 'Have posted in the last 30 days',
      'zh-CN': '过去 30 天内有发帖',
      ja: '過去 30 日以内に投稿している',
    },
    posted3m: {
      en: 'Have posted in the last 3 months',
      'zh-CN': '过去 3 个月内有发帖',
      ja: '過去 3 ヶ月以内に投稿している',
    },
  },

  // Hub / monetization entry
  monetization: {
    title: { en: 'Make money on Kommend', 'zh-CN': '在 Kommend 上变现', ja: 'Kommend で収益化' },
    subtitle: {
      en: 'Choose how you want to earn from your audience.',
      'zh-CN': '选择你想要的变现方式。',
      ja: '収益化の方法を選びましょう。',
    },
    getStarted: { en: 'Get started', 'zh-CN': '立即开始', ja: '始める' },
    checkEligibility: { en: 'Check eligibility', 'zh-CN': '查看资格', ja: '資格を確認' },
    setUpTiers: { en: 'Set up tiers', 'zh-CN': '设置订阅等级', ja: '階層を設定' },
    eligibleBadge: { en: 'Eligible', 'zh-CN': '已合格', ja: '資格あり' },
    notEligibleBadge: { en: 'Not eligible', 'zh-CN': '未达标', ja: '未達成' },
    revenueSharing: {
      title: { en: 'Get paid to post', 'zh-CN': '帖子分成', ja: '投稿で報酬' },
      desc: {
        en: 'Earn from sharing high quality content. The more Kommend members engage with your posts, the more you earn.',
        'zh-CN': '通过创作高质量内容赚钱。Kommend 会员互动越多，分成越多。',
        ja: '質の高い投稿で収益化。Kommend メンバーとの交流が多いほど報酬が増えます。',
      },
    },
    subscriptions: {
      title: { en: 'Build a fanbase', 'zh-CN': '粉丝订阅', ja: 'ファンベース構築' },
      desc: {
        en: 'Offer exclusive content to your biggest supporters and earn recurring income.',
        'zh-CN': '为铁粉提供专享内容，获得稳定的月度订阅收入。',
        ja: 'コアファンに限定コンテンツを提供し、継続収益を得ましょう。',
      },
    },
    eligible: {
      title: { en: "You're eligible!", 'zh-CN': '已符合资格！', ja: '資格を満たしています！' },
      desc: {
        en: 'You meet all requirements for {{program}}. Set up to start earning.',
        'zh-CN': '你已满足 {{program}} 的所有门槛，立即开始变现。',
        ja: '{{program}} のすべての要件を満たしています。',
      },
    },
    notEligible: {
      title: { en: 'Check back later', 'zh-CN': '稍后再来', ja: 'もう少し後で' },
      desc: {
        en: "You're not yet eligible for {{program}}. Meet the below requirements to participate:",
        'zh-CN': '你还没有满足 {{program}} 的所有门槛。达到下列要求即可参与：',
        ja: 'まだ {{program}} の要件を満たしていません。以下を達成してください：',
      },
    },
  },

  // Generic
  subscribe: { en: 'Subscribe', 'zh-CN': '订阅', ja: 'サブスク' },
  manageSubscription: { en: 'Manage subscription', 'zh-CN': '管理订阅', ja: 'サブスク管理' },
  cancel: { en: 'Cancel', 'zh-CN': '取消', ja: 'キャンセル' },
  upgrade: { en: 'Upgrade', 'zh-CN': '升级', ja: 'アップグレード' },
  downgrade: { en: 'Downgrade', 'zh-CN': '降级', ja: 'ダウングレード' },
  perMonth: { en: '/ month', 'zh-CN': '/月', ja: '/月' },

  // Statuses
  status: {
    active: { en: 'Active', 'zh-CN': '正常', ja: 'アクティブ' },
    pastDue: { en: 'Past due', 'zh-CN': '余额不足', ja: '支払い遅延' },
    suspended: { en: 'Suspended', 'zh-CN': '已暂停', ja: '停止中' },
    cancelled: { en: 'Cancelled', 'zh-CN': '已取消', ja: 'キャンセル済' },
  },

  // Approval
  approval: {
    pending: { en: 'Awaiting 1st sig', 'zh-CN': '待第一个批准', ja: '1次承認待ち' },
    firstApproved: { en: 'Awaiting 2nd sig', 'zh-CN': '待第二个批准', ja: '2次承認待ち' },
    fullyApproved: { en: 'Fully approved', 'zh-CN': '已全部批准', ja: '完全承認済' },
    rejected: { en: 'Rejected', 'zh-CN': '已驳回', ja: '却下' },
    expired: { en: 'Expired', 'zh-CN': '已过期', ja: '期限切れ' },
  },

  // Fan pages
  fan: {
    mySubscriptions: { en: 'My subscriptions', 'zh-CN': '我的订阅', ja: 'マイサブスク' },
    nextBilling: { en: 'Next billing', 'zh-CN': '下次扣款', ja: '次回課金' },
    noSubscriptions: {
      en: "You haven't subscribed to anyone yet.",
      'zh-CN': '你还没有订阅任何创作者。',
      ja: 'まだサブスク中のクリエイターはいません。',
    },
    subscribeTo: { en: 'Subscribe to {{name}}', 'zh-CN': '订阅 {{name}}', ja: '{{name}} をサブスク' },
    membersOnlyContent: { en: 'Members-only content', 'zh-CN': '会员专享内容', ja: '会員限定コンテンツ' },
    subscribeToUnlock: {
      en: 'Subscribe to unlock this post.',
      'zh-CN': '订阅以解锁此内容。',
      ja: 'サブスクして投稿を解放しましょう。',
    },
  },

  // Perks
  perks: {
    badge: { en: 'Subscriber badge', 'zh-CN': '订阅者徽章', ja: 'サブスクバッジ' },
    exclusivePosts: { en: 'Members-only posts', 'zh-CN': '会员专享帖子', ja: '会員限定投稿' },
    subscriberOnlySpaces: { en: 'Subscriber-only Spaces', 'zh-CN': '会员专享语音房', ja: '会員限定スペース' },
    highlightedComments: { en: 'Highlighted comments', 'zh-CN': '加亮评论', ja: 'ハイライトコメント' },
    communityAccess: { en: 'Community group access', 'zh-CN': '会员社群入口', ja: 'コミュニティアクセス' },
    customEmoji: { en: 'Custom emoji', 'zh-CN': '自定义 emoji', ja: 'カスタム絵文字' },
    subscriberOnlyReplies: { en: 'Subscriber-only replies', 'zh-CN': '会员专享回帖', ja: '会員限定リプライ' },
  },

  // Studio
  studio: {
    tiers: { en: 'Subscription tiers', 'zh-CN': '订阅等级', ja: 'サブスク階層' },
    newTier: { en: '+ New tier', 'zh-CN': '+ 新建等级', ja: '+ 新規階層' },
    deactivate: { en: 'Deactivate', 'zh-CN': '停用', ja: '無効化' },
    edit: { en: 'Edit', 'zh-CN': '编辑', ja: '編集' },
    deactivateConfirm: {
      en: 'Deactivate this tier? Existing subscribers continue until their cycle ends.',
      'zh-CN': '停用此等级？已订阅者保留到月底。',
      ja: '無効化しますか？既存ユーザーはサイクル終了まで継続します。',
    },
    members: { en: 'Members', 'zh-CN': '会员', ja: 'メンバー' },
    payouts: { en: 'Payouts', 'zh-CN': '结算', ja: 'ペイアウト' },
    payoutHistory: { en: 'Payout history', 'zh-CN': '结算历史', ja: 'ペイアウト履歴' },
    stats: { en: 'Subscription stats', 'zh-CN': '订阅数据', ja: 'サブスク統計' },
    activeMembers: { en: 'Active members', 'zh-CN': '活跃会员', ja: 'アクティブ会員' },
    pastDue: { en: 'Past due', 'zh-CN': '余额不足', ja: '支払い遅延' },
    tierBreakdown: { en: 'Tier breakdown', 'zh-CN': '等级分布', ja: '階層別内訳' },
    noPayouts: { en: 'No payouts yet.', 'zh-CN': '暂无结算记录。', ja: 'ペイアウトはまだありません。' },
    noMembers: { en: 'No subscribers yet.', 'zh-CN': '还没有订阅者。', ja: 'まだサブスクはありません。' },
    noTiersYet: {
      en: "You haven't created any tiers yet. Click \"New tier\" to start offering subscriptions.",
      'zh-CN': '你还没创建任何等级。点击"新建等级"开始接受订阅。',
      ja: 'まだ階層を作成していません。"新規階層"をクリックして始めましょう。',
    },
    inactive: { en: 'Inactive', 'zh-CN': '已停用', ja: '無効' },
    back: { en: '← Back', 'zh-CN': '← 返回', ja: '← 戻る' },
    membersCount: { en: 'Members ({{count}})', 'zh-CN': '会员（{{count}}）', ja: 'メンバー（{{count}}）' },
    joined: { en: 'Joined', 'zh-CN': '加入于', ja: '加入日' },
    txHash: { en: 'tx', 'zh-CN': 'tx', ja: 'tx' },
    paid: { en: 'paid', 'zh-CN': '已支付', ja: '支払済' },
    activeMembersStat: { en: 'Active members', 'zh-CN': '活跃会员', ja: 'アクティブ会員' },
    pastDueStat: { en: 'Past due', 'zh-CN': '余额不足', ja: '支払い遅延' },
  },

  // Tier editor
  tierEditor: {
    create: { en: 'Create new tier', 'zh-CN': '新建等级', ja: '新しい階層を作成' },
    editing: { en: 'Edit tier', 'zh-CN': '编辑等级', ja: '階層を編集' },
    name: { en: 'Tier name', 'zh-CN': '等级名称', ja: '階層名' },
    namePlaceholder: { en: 'e.g. Basic / Premium / VIP', 'zh-CN': '例：基础/高级/铁粉', ja: '例: ベーシック / プレミアム' },
    description: { en: 'Description (optional)', 'zh-CN': '描述（选填）', ja: '説明（任意）' },
    price: { en: 'Price (USD/month)', 'zh-CN': '价格（美元/月）', ja: '価格（USD/月）' },
    priceLockedV1: {
      en: 'Price cannot be changed in V1.',
      'zh-CN': 'V1 不支持改价。',
      ja: 'V1では価格変更不可。',
    },
    displayOrder: { en: 'Display order', 'zh-CN': '显示顺序', ja: '表示順' },
    perksLabel: { en: 'Perks', 'zh-CN': '权益', ja: '特典' },
    saveChanges: { en: 'Save changes', 'zh-CN': '保存修改', ja: '変更を保存' },
    createButton: { en: 'Create tier', 'zh-CN': '创建等级', ja: '階層を作成' },
  },

  // Admin
  admin: {
    pendingApprovalQueue: {
      en: 'Pending payout approval ({{count}})',
      'zh-CN': '待审批结算（{{count}}）',
      ja: '承認待ちペイアウト（{{count}}）',
    },
    noPending: {
      en: 'No payouts awaiting approval.',
      'zh-CN': '没有待批准的结算。',
      ja: '承認待ちのペイアウトはありません。',
    },
    approve: { en: 'Approve', 'zh-CN': '批准', ja: '承認' },
    reject: { en: 'Reject', 'zh-CN': '驳回', ja: '却下' },
    rejectReason: { en: 'Reject reason?', 'zh-CN': '驳回原因？', ja: '却下理由は？' },
    config: { en: 'Subscription config', 'zh-CN': '订阅配置', ja: 'サブスク設定' },
    locked: { en: 'Locked', 'zh-CN': '已锁定', ja: 'ロック中' },
    save: { en: 'Save', 'zh-CN': '保存', ja: '保存' },
    cancel: { en: 'Cancel', 'zh-CN': '取消', ja: 'キャンセル' },
    reasonPlaceholder: {
      en: 'Reason (optional, for audit)',
      'zh-CN': '修改原因（选填，记录审计）',
      ja: '理由（任意・監査用）',
    },
    platformStats: { en: 'Platform stats', 'zh-CN': '平台总览', ja: 'プラットフォーム統計' },
    activeSubscriptions: { en: 'Active subscriptions', 'zh-CN': '活跃订阅', ja: 'アクティブサブスク' },
    activeCreators: { en: 'Active creators', 'zh-CN': '活跃创作者', ja: 'アクティブクリエイター' },
    pendingPayouts: { en: 'Pending payouts', 'zh-CN': '待结算', ja: '承認待ちペイアウト' },
    currentMonthGmv: { en: 'Current month GMV', 'zh-CN': '本月 GMV', ja: '今月のGMV' },
    platformCut: { en: 'Platform cut', 'zh-CN': '平台抽成', ja: 'プラットフォーム手数料' },
  },
};
